<template>
  <div class="app-substitute-sim" :class="['step-' + this.getCurrentStep]">
    <div class="main-content">
      <div class="container">
        <StepsBar v-if="!skippedSelection" :current-step="getCurrentStep" :steps="steps" @onStepClick="changeStep">
          <template v-for="slot in possibleChildSlotsFor('CheckoutSteps')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </StepsBar>
        <div v-if="getCurrentStep === 'simData'">
          <Selection
                        :ref="getCurrentStep"
                        @goForward="goToNextStep"
                        :backLink="backLink">
            <template v-for="simSlot in possibleChildSlotsFor('SimData')" v-slot:[`${simSlot.parent}`]>
              <slot :name="`${simSlot.parent}`"></slot>
            </template>
            <template v-for="slot in possibleChildSlotsFor('SimActivationData')" v-slot:[`${slot.parent}`]>
              <slot :name="`${slot.parent}`"></slot>
            </template>
          </Selection>
        </div>
        <Summary
          @goBack="goToPreviousStep"
          @goForward="goToNextStep"
          @onStepClick="changeStep"
          :isESIMFree="this.productPrice === 0"
          :usesESIM="currentCustomer.contract.simCard.type === 'E_SIM'"
          :backLink="backLink"
          :successUrlSim="successUrlSim"
          :successUrlEsim="successUrlEsim"
          :msisdn="currentCustomer.contract.msisdn"
          v-if="getCurrentStep === 'summary'">
          <template v-for="slot in possibleChildSlotsFor('Summary')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
          <template v-for="slot in possibleChildSlotsFor('Mfa')" v-slot:[`${slot.parent}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
          <template v-for="cartSlot in possibleChildSlotsFor('Cart')" v-slot:[`${cartSlot.parent}`]>
            <slot :name="`${cartSlot.parent}`"></slot>
          </template>
        </Summary>
        <StickyCart
          v-if="getCurrentStep !== 'summary'"
          :fee="productPrice">
          <template v-for="slot in possibleChildSlotsFor('Cart')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </StickyCart>
      </div>
    </div>
    <div id="cart-sidebar" class="sidebar">
      <div class="container">
        <Cart process="substituteSim"
              :showFreeESIMMessage="this.productPrice === 0 && this.selectedSim.type === 'E_SIM'"
              class="card">
          <template v-for="slot in possibleChildSlotsFor('Cart')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </Cart>
      </div>
      <Stepper
        class="mobile-stepper"
        :loading-next="loading"
        :show-prev=true
        @prev=goToPreviousStep
        @next="next()"
      >
        <template v-for="slot in possibleChildSlotsFor(mobileStepperSlots)" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </Stepper>
    </div>
  </div>
</template>

<script>
import { substituteSimTracking } from '@/helper/checkoutTracking'
import { mapMutations } from 'vuex'
import trackingHandler from '@/mixins/trackingHandler'
import Stepper from '@/components/generic/Stepper.vue'
import transaction from '@/mixins/transaction'
import date from '@/mixins/validators/date'
import Cart from '@/components/checkout/substituteSim/Cart.vue'
import Selection from '@/components/checkout/substituteSim/Selection.vue'
import Summary from '@/components/checkout/substituteSim/summary/Summary.vue'
import StickyCart from '@/components/checkout/substituteSim/StickyCart.vue'
import StepsBar from '@/components/checkout/StepsBar.vue'
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'

export default {
  name: 'SubstituteSim',
  components: { Stepper, Cart, Selection, Summary, StickyCart, StepsBar },
  props: {
    backLink: {
      type: String,
      default: '/mein-simon/sim'
    },
    successUrlSim: {
      type: String,
      default: '/sim/ersatz/danke-sim'
    },
    successUrlEsim: {
      type: String,
      default: '/sim/ersatz/danke'
    }
  },
  data () {
    return {
      steps: ['simData', 'summary'],
      loading: false,
      currentCustomer: null,
      substituteSimCardOptions: []
    }
  },
  mounted () {
    this.triggerPageviewTracking(this.steps.indexOf(this.getCurrentStep))
  },
  mixins: [
    trackingHandler,
    transaction('substituteSim'),
    date
  ],
  methods: {
    ...mapMutations('checkoutv2/substituteSim', ['setStep', 'setSubstituteSimCardOptions']),
    ...mapMutations('checkoutv2/substituteSim/_simData', ['setSimType']),
    ...mapMutations('checkoutv2/substituteSim', ['setSimType', 'setSubstituteSimCardOptions']),
    changeStep (step) {
      this.setStep(step)
      this.triggerPageviewTracking(this.steps.indexOf(step))
      window.scrollTo(0, 0)
      this.$nextTick(() => {
        this.focusStep()
      })
    },
    goToPreviousStep () {
      if (this.getCurrentStep === 'simData' || this.skippedSelection) {
        window.location.href = '/mein-simon/sim'
      } else {
        const newIndex = Math.max(0, (this.steps.indexOf(this.getCurrentStep) - 1))
        this.step(newIndex)
        this.$nextTick(() => {
          this.focusStep()
        })
      }
    },
    goToNextStep () {
      const newIndex = Math.min((this.steps.length - 1), (this.steps.indexOf(this.getCurrentStep) + 1))
      this.step(newIndex)
      this.$nextTick(() => {
        this.focusStep()
      })
    },
    step (newIndex) {
      this.setStep(this.steps[newIndex])
      this.triggerPageviewTracking(newIndex)
      window.scrollTo(0, 0)
    },
    focusStep () {
      const spanElement = document.querySelector('.' + this.getCurrentStep + ' .label')
      spanElement.focus()
    },
    next () {
      this.$refs[this.getCurrentStep].save()
    },
    triggerPageviewTracking (stepIndex = this.steps.indexOf(this.getCurrentStep)) {
      substituteSimTracking(this.$store, stepIndex + 1)
    }
  },
  computed: {
    getCurrentStep: function () {
      return this.$store.getters['checkoutv2/substituteSim/step']
    },
    selectedSim: function () {
      return this.$store.getters['checkoutv2/substituteSim/selectedSim']
    },
    skippedSelection: function () {
      return this.$store.getters['checkoutv2/substituteSim/skippedSelection']
    },
    productPrice: function () {
      return this.selectedSim?.productPrice ? this.selectedSim?.productPrice : 0
    },
    mobileStepperSlots: function () {
      if (this.getCurrentStep === 'simData') {
        return 'MnpStepper' + this.getCurrentStep.charAt(0).toUpperCase() + this.getCurrentStep.slice(1)
      } else {
        return 'Stepper' + this.getCurrentStep.charAt(0).toUpperCase() + this.getCurrentStep.slice(1)
      }
    }
  },
  apollo: {
    contractData: function () {
      return wrapQuery(
        gql`query simData {
            currentCustomer {
                contract {
                    msisdn
                    simCard {
                        type
                    }
               }
            }
            substituteSimCardOptions {
              name,
              price,
              productNumber,
              productPrice,
              shippingCosts,
              total,
              type,
              possibleActivationSettings
            }
        }`, this, ['currentCustomer', 'substituteSimCardOptions'], {}, (response) => {
          const simOptions = this.substituteSimCardOptions
          if (typeof simOptions !== 'undefined' && simOptions.length > 0) {
            this.setSubstituteSimCardOptions(simOptions)
          }

          this.stopLoader()
        }, { fetchPolicy: 'cache-first' }, (error) => {
          this.triggerErrorTracking(error)
          this.errorToast(this.getErrorMessage(error))
        }, (error) => {
          console.log(error)
          this.loading = false
          this.stopLoader()
        }
      )
    }
  }
}
</script>
